// RescheduleNormalModal.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import moment from "moment-jalaali";
import axios from "axios";
import {
  X,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Lock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Check,
  Clock,
  XCircle,
  ArrowLeft,
  ArrowRight,
  User,
  Users,
  Clover,
} from "lucide-react";
import { SET_ACCESS_TOKEN } from "../../../../../redux/action/auth/type";
import { setUserAppointments } from "../../../../../redux/action/user/appointments/action"; // ← مسیر را اصلاح کنید

const BASE_URL = "https://my-sarad-back.liara.run";

const persianMonths = [
  { id: 1, name: "فروردین", days: 31 },
  { id: 2, name: "اردیبهشت", days: 31 },
  { id: 3, name: "خرداد", days: 31 },
  { id: 4, name: "تیر", days: 31 },
  { id: 5, name: "مرداد", days: 31 },
  { id: 6, name: "شهریور", days: 31 },
  { id: 7, name: "مهر", days: 30 },
  { id: 8, name: "آبان", days: 30 },
  { id: 9, name: "آذر", days: 30 },
  { id: 10, name: "دی", days: 30 },
  { id: 11, name: "بهمن", days: 30 },
  { id: 12, name: "اسفند", days: 29 },
];

const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

const reasonLabelMap = {
  SALON_HOLIDAY: "تعطیلی سالن",
  STAFF_HOLIDAY: "تعطیلی مژه‌کار",
  STAFF_NOT_WORKING: "روز کاری نیست",
  FULLY_BOOKED: "ظرفیت تکمیل",
  NO_STAFF: "هیچ نیرویی موجود نیست",
  NO_STAFF_WORKING: "هیچ نیرویی کار نمی‌کند",
};

const isLeapYear = (year) => {
  const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
  return breaks.includes(year % 33);
};

const getDaysInMonth = (month, year) => {
  if (!month || month < 1 || month > 12) return 30;
  if (month === 12 && isLeapYear(year)) return 30;
  return persianMonths[month - 1].days;
};

const toDateOnly = (value) => (value ? String(value).split("T")[0] : "");

const addTime = (timeStr, hours, minutes) => {
  const [h, m] = timeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(h + hours, m + minutes, 0, 0);
  return `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
};

// تابع انتخاب رندوم نیرو با اولویت کمترین تعداد قرار
const pickRandomStaffWithLowestLoad = (staffs) => {
  if (!staffs || staffs.length === 0) return null;
  if (staffs.length === 1) return staffs[0];

  // پیدا کردن کمترین appointmentCount
  const minCount = Math.min(...staffs.map((s) => s.appointmentCount ?? 0));
  // فیلتر کردن نیروهایی که کمترین قرار را دارند
  const leastLoaded = staffs.filter(
    (s) => (s.appointmentCount ?? 0) === minCount
  );
  // انتخاب رندوم از بین آن‌ها
  const randomIndex = Math.floor(Math.random() * leastLoaded.length);
  return leastLoaded[randomIndex];
};

const RescheduleNormalModal = ({ appointment, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [cookies] = useCookies();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const serverTime = useSelector(
    (state) => state.info?.dateNowServer?.serverTime
  );

  // State ها
  const [loading, setLoading] = useState(true);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [dayDetails, setDayDetails] = useState(null);
  const [fetchingDay, setFetchingDay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [todayCutoffTime, setTodayCutoffTime] = useState(null);
  const [nowDate, setNowDate] = useState(null);

  const timeSectionRef = useRef(null);

  // تاریخ امروز
  const today = useMemo(() => {
    return serverTime
      ? moment(serverTime).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
  }, [serverTime]);

  // تاریخ و زمان رزرو فعلی
  const currentAppointmentDate = appointment?.datetime?.date;
  const currentAppointmentTime = appointment?.datetime?.time;
  const serviceId = appointment?.service?._id;
  const serviceType = appointment?.service?.type;

  const appointments = useSelector((state) => state.userAppointments?.appointments || []);

  // غیرفعال کردن اسکرول صفحه هنگام باز شدن مودال
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // تابع fetchWithAuth برای احراز هویت
  const fetchWithAuth = useCallback(
    async ({ method = "post", url, data, params }) => {
      try {
        const config = {
          method,
          url: `${BASE_URL}${url}`,
          headers: { Authorization: `Bearer ${accessToken}` },
        };
        
        if (data && (method === "post" || method === "put")) {
          config.data = data;
        }
        
        if (params && (method === "get" || method === "delete")) {
          config.params = params;
        }

        const res = await axios(config);
        return res.data;
      } catch (error) {
        if (error.response?.status === 401) {
          try {
            const refreshRes = await axios.post(
              `${BASE_URL}/api/auth/refresh-token`,
              { refreshToken: cookies.refreshToken }
            );
            const newAccess = refreshRes.data.accessToken;
            dispatch({ type: SET_ACCESS_TOKEN, payload: newAccess });

            // تکرار درخواست اصلی با توکن جدید
            const retryConfig = {
              method,
              url: `${BASE_URL}${url}`,
              headers: { Authorization: `Bearer ${newAccess}` },
            };
            
            if (data && (method === "post" || method === "put")) {
              retryConfig.data = data;
            }
            
            if (params && (method === "get" || method === "delete")) {
              retryConfig.params = params;
            }

            const retryRes = await axios(retryConfig);
            return retryRes.data;
          } catch (refreshError) {
            toast.error("نشست شما منقضی شده است، لطفا دوباره وارد شوید");
            throw refreshError;
          }
        } else {
          throw error;
        }
      }
    },
    [accessToken, cookies.refreshToken, dispatch]
  );

  // گرفتن availability data (کل تقویم)
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        setLoading(true);
        
        // API اولیه برای گرفتن کل تقویم (عادی)
        const data = await fetchWithAuth({
          method: "post",
          url: "/api/user/availability/normal",
          data: {
            serviceId: serviceId,
            type: serviceType,
            days: 180,
          },
        });

        setNowDate(data.dateNowServer?.serverTime);
        setAvailabilityData(data);

        // تنظیم ماه و سال بر اساس تاریخ رزرو فعلی
        const appointmentMoment = moment(currentAppointmentDate, "YYYY-MM-DD");
        setSelectedYear(appointmentMoment.jYear());
        setSelectedMonth(appointmentMoment.jMonth() + 1);

        // تنظیم تاریخ پیش‌فرض
        const jalaaliDate = appointmentMoment.format("jYYYY/jMM/jDD");
        const defaultDateObj = {
          date: currentAppointmentDate,
          jalaaliDate,
          gregorianDate: currentAppointmentDate,
          isToday: currentAppointmentDate === today,
          isAvailable: true,
          isSelected: true,
          isCurrentAppointment: true,
        };
        
        setSelectedDate(defaultDateObj);
        setSelectedTime(currentAppointmentTime);

        // تنظیم نیروی فعلی
        if (appointment?.staff) {
          setSelectedStaff(appointment.staff);
        }

      } catch (error) {
        console.error("Error fetching availability:", error);
        toast.error("خطا در دریافت اطلاعات زمان‌بندی");
      } finally {
        setLoading(false);
      }
    };

    if (appointment && serviceId && serviceType) {
      fetchAvailability();
    }
  }, [appointment, fetchWithAuth, currentAppointmentDate, currentAppointmentTime, today, serviceId, serviceType]);

  // گرفتن جزئیات روز برای تاریخ انتخاب شده
  const fetchDayAvailability = useCallback(async (date) => {
    if (!serviceId || !serviceType || !date) return;
    
    setFetchingDay(true);
    setTodayCutoffTime(null);
    setDayDetails(null);
    setSelectedStaff(null);
    
    try {
      const response = await fetchWithAuth({
        method: "get",
        url: "/api/user/availability/day/normal",
        params: {
          serviceId: serviceId,
          type: serviceType,
          date: date,
        },
      });

      console.log("Day details response:", response); // برای دیباگ

      // دریافت serverTime از پاسخ و محاسبه cutoff time
      const rawServerTime = response?.serverTime || response?.data?.serverTime;
      if (rawServerTime && date === today) {
        // جدا کردن تاریخ و قسمت زمان
        const [datePart, timePartWithMs] = rawServerTime.split("T");
        const timePart = timePartWithMs ? timePartWithMs.split(".")[0] : "";
        const [hh, mm] = timePart.split(":").map(Number);

        // ساخت رشته زمان به صورت HH:MM
        const serverTimeHHMM = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;

        // افزودن ۳ ساعت و ۳۰ دقیقه
        const cutoff = addTime(serverTimeHHMM, 3, 30);
        setTodayCutoffTime(cutoff);
      }

      // بررسی ساختار داده‌های دریافتی
      const dayData = response?.data || response;
      
      if (dayData) {
        // اطمینان از وجود آرایه‌های مورد نیاز
        const processedData = {
          workTimes: dayData.availableTimes || [],
          timeSlots: dayData.timeSlots || [],
          isAvailable: dayData.isAvailable !== undefined ? dayData.isAvailable : true,
          date: dayData.date || date,
          serverTime: dayData.serverTime,
          reasons: dayData.reasons || [],
          weekDay: dayData.weekDay,
          weekDayFa: dayData.weekDayFa,
          unavailableTimes: dayData.unavailableTimes || [],
        };
        
        setDayDetails(processedData);
      } else {
        toast.error("داده‌های روز نامعتبر است");
      }
    } catch (error) {
      console.error("Error fetching day details:", error);
      toast.error("خطا در دریافت اطلاعات روز");
    } finally {
      setFetchingDay(false);
    }
  }, [fetchWithAuth, serviceId, serviceType, today]);

  // وقتی روز انتخاب‌شده تغییر کرد، تایم‌های آن روز را بگیر
  useEffect(() => {
    if (selectedDate?.date) {
      fetchDayAvailability(selectedDate.date);
    }
  }, [selectedDate, fetchDayAvailability]);

  // ساخت مپ‌های available و unavailable
  const { availableMap, unavailableMap } = useMemo(() => {
    if (!availabilityData?.availability) return { availableMap: {}, unavailableMap: {} };

    const available = {};
    const unavailable = {};

    availabilityData.availability.availableDates?.forEach((date) => {
      const dateOnly = toDateOnly(date.date);
      available[dateOnly] = {
        date: dateOnly,
        weekDay: date.weekDay,
        weekDayFa: date.weekDayFa,
        ...date
      };
    });

    availabilityData.availability.unavailableDates?.forEach((date) => {
      const dateOnly = toDateOnly(date.date);
      unavailable[dateOnly] = {
        reasons: date.reasons,
        date: dateOnly,
        ...date
      };
    });

    return { availableMap: available, unavailableMap: unavailable };
  }, [availabilityData]);

  // تولید روزهای ماه
  const calendarDays = useMemo(() => {
    if (!selectedMonth || !selectedYear) return [];

    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDayOfMonth = moment(`${selectedYear}/${selectedMonth}/1`, "jYYYY/jM/jD");
    const startDayOfWeek = firstDayOfMonth.day() + 1;
    const days = [];

    // روزهای خالی قبل از ماه
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // روزهای ماه
    for (let day = 1; day <= daysInMonth; day++) {
      const jalaaliDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD").format("jYYYY/jMM/jDD");
      const gregorianDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD").format("YYYY-MM-DD");
      const availableItem = availableMap[gregorianDate];
      const unavailableItem = unavailableMap[gregorianDate];
      
      days.push({
        day,
        jalaaliDate,
        gregorianDate,
        isToday: gregorianDate === today,
        isAvailable: Boolean(availableItem),
        isUnavailable: Boolean(unavailableItem),
        isSelected: selectedDate?.date === gregorianDate,
        isCurrentAppointment: currentAppointmentDate === gregorianDate,
        availableItem,
        unavailableItem,
      });
    }

    return days;
  }, [selectedMonth, selectedYear, availableMap, unavailableMap, today, selectedDate, currentAppointmentDate]);

  const currentMonthName = persianMonths[selectedMonth - 1]?.name || "نامشخص";

  // تغییر ماه
  const changeMonth = (dir) => {
    if (dir === "next") {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear((y) => y + 1);
      } else {
        setSelectedMonth((m) => m + 1);
      }
    } else {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear((y) => y - 1);
      } else {
        setSelectedMonth((m) => m - 1);
      }
    }
  };

  // انتخاب روز
  const handleSelectDay = async (dateObj) => {
    if (!dateObj || !dateObj.isAvailable) return;

    const payload = {
      date: dateObj.gregorianDate,
      jalaaliDate: dateObj.jalaaliDate,
      ...dateObj.availableItem,
      isCurrentAppointment: dateObj.gregorianDate === currentAppointmentDate,
    };
    
    setSelectedDate(payload);
    setSelectedTime(null);
    setSelectedStaff(null);

    // اسکرول به بخش انتخاب زمان
    setTimeout(() => {
      timeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // فیلتر تایم‌های قابل انتخاب
  const augmentedTimeSlots = useMemo(() => {
    if (!dayDetails?.timeSlots || !Array.isArray(dayDetails.timeSlots)) return [];

    const { timeSlots = [] } = dayDetails;

    return timeSlots.map((slot) => {
      const isCurrentAppointmentTime = selectedDate?.date === currentAppointmentDate && slot.time === currentAppointmentTime;
      
      // بررسی cutoff time برای امروز
      let isPast = false;
      if (selectedDate?.date === today && todayCutoffTime && slot.time) {
        isPast = slot.time < todayCutoffTime;
      }
      
      // اگر تایم فعلی رزرو باشد، حتی اگر available نباشد باز هم فعال است
      const disabled = isCurrentAppointmentTime ? false : (!slot.available || isPast);

      return {
        time: slot.time,
        disabled,
        isPast,
        isCurrentAppointmentTime,
        isAvailable: slot.available,
        staffs: slot.staffs || [],
        appointmentCount: slot.appointmentCount || 0,
      };
    });
  }, [dayDetails, todayCutoffTime, today, selectedDate, currentAppointmentDate, currentAppointmentTime]);

  // انتخاب زمان و انتخاب رندوم نیرو
  const handleTimeSelect = (timeSlot) => {
    if (!timeSlot || (timeSlot.disabled && !timeSlot.isCurrentAppointmentTime)) return;
    
    setSelectedTime(timeSlot.time);
    
    // انتخاب رندوم نیرو از بین نیروهای موجود برای این تایم
    if (timeSlot.staffs && timeSlot.staffs.length > 0) {
      const randomStaff = pickRandomStaffWithLowestLoad(timeSlot.staffs);
      setSelectedStaff(randomStaff);
    } else {
      setSelectedStaff(null);
    }
  };

  // بررسی تغییر داده‌ها
  const hasChanges = useMemo(() => {
    if (!selectedDate || !selectedTime || !selectedStaff) return false;
    return (
      selectedDate.date !== currentAppointmentDate ||
      selectedTime !== currentAppointmentTime ||
      selectedStaff._id !== appointment?.staff?._id
    );
  }, [selectedDate, selectedTime, selectedStaff, currentAppointmentDate, currentAppointmentTime, appointment?.staff?._id]);

  // ذخیره تغییرات
 // ذخیره تغییرات
const handleSave = async () => {
  if (!hasChanges || !selectedDate || !selectedTime || !selectedStaff) return;

  try {
    setSubmitting(true);

    const response = await fetchWithAuth({
      method: "put",
      url: `/api/user/appointments/${appointment._id}/reschedule`,
      data: {
        newDate: selectedDate.date,
        newTime: selectedTime,
        newStaffId: selectedStaff._id,
        newMethod: "عادی",
      },
    });

    // بعد از موفقیت، نوبت به‌روزرسانی‌شده را از پاسخ می‌گیریم
    const updatedAppointment = response.appointment; // fetchWithAuth خودش res.data را برمی‌گرداند

    // آپدیت نوبت‌ها در Redux
    if (appointments.length > 0) {
      const updatedAppointments = appointments.map((apt) =>
        apt._id === appointment._id ? updatedAppointment : apt
      );
      dispatch(setUserAppointments(updatedAppointments));
    } else {
      // اگر آرایه خالی بود، همان یک نوبت جدید را دیسپچ می‌کنیم
      dispatch(setUserAppointments([updatedAppointment]));
    }

    toast.success("زمان رزرو با موفقیت تغییر یافت");

    // در صورت وجود callback برای بستن مودال یا رفرش والد
    if (onSuccess) {
      onSuccess(updatedAppointment);
    }
  } catch (error) {
    console.error("Error rescheduling:", error);
    const message = error?.response?.data?.message || error?.message || "خطا در تغییر زمان رزرو";
    toast.error(message);
  } finally {
    setSubmitting(false);
  }
};

  // وقتی روز انتخاب‌شده تغییر کرد، تایم‌های آن روز را بگیر
  useEffect(() => {
    if (selectedDate && selectedDate.date === currentAppointmentDate) {
      // اگر روز همان روز رزرو فعلی است، تایم فعلی را انتخاب کن
      setSelectedTime(currentAppointmentTime);
      // و نیروی فعلی را هم انتخاب کن
      if (appointment?.staff) {
        setSelectedStaff(appointment.staff);
      }
    } else {
      setSelectedTime(null);
      setSelectedStaff(null);
    }
  }, [selectedDate, currentAppointmentDate, currentAppointmentTime, appointment?.staff]);

  const getUnavailableReasonText = (dateObj) => {
    const reasons = dateObj?.unavailableItem?.reasons || [];
    if (!reasons.length) return "غیرقابل رزرو";
    return reasons.map((r) => reasonLabelMap[r.code] || r.message).join("، ");
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            <span className="mr-2 text-gray-600">در حال بارگذاری...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r z-50 from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">تغییر زمان رزرو (عادی)</h2>
              <p className="text-blue-100 text-sm mt-0.5">
                {appointment?.service?.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* اطلاعات رزرو فعلی */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-5 shadow-sm relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-16 h-16 rounded-full bg-blue-200 opacity-30"></div>
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-blue-800 text-lg">رزرو فعلی شما</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    {moment(currentAppointmentDate).format("jYYYY/jMM/jDD")} - ساعت {currentAppointmentTime}
                  </p>
              
                </div>
              </div>
            </div>
          </div>

          {/* تقویم */}
          <div className="bg-white max-w-2xl mx-auto border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            {/* Calendar Header */}
            <div className="bg-slate-900 px-4 md:px-5 py-4 flex items-center justify-between">
              <button
                onClick={() => changeMonth("prev")}
                className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-lg md:text-xl font-black text-white">
                  {currentMonthName} {selectedYear}
                </h3>
              </div>
              <button
                onClick={() => changeMonth("next")}
                className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Week Days */}
            <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
              {weekDays.map((day) => (
                <div key={day} className="py-2 md:py-3 text-center text-xs md:text-sm font-bold text-slate-600">
                  {day}
                </div>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1 md:gap-2 p-2 md:p-4 bg-white">
              {calendarDays.map((dateObj, index) => {
                if (!dateObj) return <div key={`empty-${index}`} className="aspect-square" />;
                
                const disabled = !dateObj.isAvailable;
                const isCurrentAppointmentDay = dateObj.gregorianDate === currentAppointmentDate;
                
                return (
                  <button
                    key={dateObj.gregorianDate}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleSelectDay(dateObj)}
                    title={
                      disabled
                        ? getUnavailableReasonText(dateObj)
                        : `قابل رزرو`
                    }
                    className={`
                      relative aspect-square rounded-xl md:rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center gap-0.5
                      text-[12px] md:text-base font-black
                      ${
                        dateObj.isSelected
                          ? "border-blue-600 bg-blue-600 text-white shadow-lg shadow-blue-500/30 scale-105"
                          : dateObj.isAvailable
                          ? "border-green-200 bg-white hover:bg-green-50 hover:border-green-400 hover:shadow-md cursor-pointer"
                          : "border-slate-200 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed"
                      }
                      ${isCurrentAppointmentDay ? "ring-2 ring-blue-500 ring-offset-1" : ""}
                    `}
                  >
                    {dateObj.isToday && (
                      <span
                        className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                          dateObj.isSelected ? "bg-white" : "bg-blue-500"
                        }`}
                      />
                    )}
                    
                    {disabled && (
                      <span className="absolute top-1 left-1">
                        <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-slate-400" />
                      </span>
                    )}
                    
                    {dateObj.isAvailable && !dateObj.isSelected && (
                      <span className="absolute top-1 left-1 hidden md:block">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                      </span>
                    )}
                    
                    <span className="text-base md:text-lg">{dateObj.day}</span>
                    
                    {!dateObj.isAvailable && (
                      <span className="hidden md:inline text-[9px] md:text-[10px] px-0.5 text-center leading-4 line-clamp-2">
                        {getUnavailableReasonText(dateObj)}
                      </span>
                    )}
                    
                    {isCurrentAppointmentDay && !dateObj.isSelected && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-blue-500"></span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* month navigation buttons */}
            <div className="flex items-center justify-between px-4 md:px-5 py-3 border-t border-slate-100 bg-slate-50/50">
              <button
                onClick={() => changeMonth("prev")}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
              >
                <ArrowRight className="w-4 h-4" />
                <span>ماه قبل</span>
              </button>
              <button
                onClick={() => changeMonth("next")}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
              >
                <span>ماه بعد</span>
                <ArrowLeft className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* انتخاب زمان */}
          {selectedDate && (
            <div ref={timeSectionRef} className="space-y-4">
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-2xl p-4">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <p className="text-sm font-semibold text-blue-800">
                  تاریخ انتخاب‌شده: {selectedDate.jalaaliDate}
                  {selectedDate.date === currentAppointmentDate && (
                    <span className="mr-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      (روز رزرو فعلی)
                    </span>
                  )}
                </p>
              </div>

              {fetchingDay ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  <span className="mr-2 text-slate-600">دریافت ساعات...</span>
                </div>
              ) : dayDetails ? (
                dayDetails.isAvailable ? (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold text-slate-700">انتخاب ساعت رزرو</h4>
                      
                    </div>
                    
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                      {augmentedTimeSlots.map((slot) => {
                        const disabled = slot.disabled;
                        const isSelected = selectedTime === slot.time;
                        
                        if (disabled && !slot.isCurrentAppointmentTime) {
                          return (
                            <div
                              key={slot.time}
                              className="relative rounded-xl border border-slate-200 bg-slate-100 p-2 text-center opacity-60 cursor-not-allowed select-none"
                              title={slot.isPast ? "گذشته" : "پر شده"}
                            >
                              <span className="text-sm font-semibold text-slate-400 line-through">
                                {slot.time}
                              </span>
                              {slot.isPast && (
                                <div className="text-[10px] text-red-400 mt-0.5">گذشت</div>
                              )}
                              {!slot.isPast && (
                                <div className="text-[10px] text-amber-500 mt-0.5">پر</div>
                              )}
                            </div>
                          );
                        }
                        
                        return (
                          <button
                            key={slot.time}
                            type="button"
                            onClick={() => handleTimeSelect(slot)}
                            className={`
                              relative rounded-xl border-2 p-2 text-center transition-all duration-200
                              ${slot.isCurrentAppointmentTime ? "ring-2 ring-blue-500 ring-offset-1" : ""}
                              ${
                                isSelected
                                  ? "border-blue-600 bg-blue-50 shadow-md shadow-blue-200"
                                  : "border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm"
                              }
                            `}
                            title={slot.isCurrentAppointmentTime ? "ساعت رزرو فعلی" : "قابل انتخاب"}
                          >
                            <span className={`text-sm font-bold ${isSelected ? "text-blue-700" : "text-slate-800"}`}>
                              {slot.time}
                            </span>
                            
                            {isSelected && (
                              <span className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">
                                ✓
                              </span>
                            )}
                            
                            {slot.isCurrentAppointmentTime && (
                              <span className="absolute -top-1 -left-1 w-5 h-5 bg-blue-500 text-white rounded-full text-[10px] flex items-center justify-center">
                                فعلی
                              </span>
                            )}
                            
                          </button>
                        );
                      })}
                    </div>
                
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-center">
                    <Lock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-700 font-medium">
                      این روز به دلایل زیر قابل رزرو نیست:
                    </p>
                    <ul className="mt-2 text-xs text-slate-600 list-disc list-inside">
                      {dayDetails.reasons?.map((r, idx) => (
                        <li key={idx}>{reasonLabelMap[r.code] || r.message}</li>
                      ))}
                    </ul>
                  </div>
                )
              ) : (
                <div className="text-center text-slate-500 py-4">
                  اطلاعات ساعتی برای این روز در دسترس نیست.
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="bg-gray-50 sm:pb-4 pb-[72px] border-t border-gray-200 px-6 py-4 flex items-center justify-between sticky bottom-0">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            بستن
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={!hasChanges || submitting || !selectedTime || !selectedStaff}
              className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-md hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  در حال ذخیره...
                </>
              ) : (
                "ذخیره تغییرات"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RescheduleNormalModal;

// // RescheduleNormalModal.jsx
// import { X } from "lucide-react";

// const RescheduleNormalModal = ({ appointment, onClose, onSuccess }) => {
//   const handleSave = () => {
//     // اینجا منطق تغییر زمان عادی اضافه خواهد شد
//     onSuccess();
//   };

//   return (
//     <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
//       {/* Backdrop */}
//       <div
//         className="absolute inset-0 bg-black/60 backdrop-blur-sm"
//         onClick={onClose}
//       />

//       {/* Modal Container */}
//       <div className="relative w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
//           <h2 className="text-lg font-bold text-white">تغییر زمان رزرو (عادی)</h2>
//           <button
//             onClick={onClose}
//             className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
//           >
//             <X className="w-5 h-5 text-white" />
//           </button>
//         </div>

//         {/* Content – خالی */}
//         <div className="p-6">
//           <p className="text-gray-600 text-center">
//             محتوای تغییر زمان عادی به‌زودی اضافه خواهد شد.
//           </p>
//         </div>

//         {/* Footer */}
//         <div className="bg-slate-50 sm:pb-4 pb-20 !flex-row-reverse border-t border-slate-200 px-6 py-4 flex items-center justify-between">
//           <button
//             onClick={handleSave}
//             className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg shadow-md hover:from-purple-700 hover:to-purple-800 transition-all"
//           >
//             ذخیره تغییرات
//           </button>
//           <button
//             onClick={onClose}
//             className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
//           >
//             بستن
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default RescheduleNormalModal;